function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { isEmpty, noop } from "lodash-es";
import { Add, Remove } from "../../icons";
import { useId, useIcon, useEventListener } from "../../helpers";
import { Label } from "../../components/label/Label";
function hasXDecimals(value, decimalPlaces) {
  const decimalValue = value.split(".");
  if (decimalValue.length > 2) return false;
  if (typeof decimalPlaces !== "number") return true;
  if (decimalPlaces === 0 && decimalValue.length === 1) return true;
  if (decimalValue.length === 2 && decimalValue[1].length > decimalPlaces) {
    return false;
  }
  return true;
}
const Input = /*#__PURE__*/React.forwardRef(function Input(_ref, ref) {
  let {
    prefixClassName,
    name,
    value,
    label,
    tip,
    onChange,
    placeholder,
    required,
    disabled,
    readOnly,
    fluid,
    onFocus,
    onBlur,
    className,
    error,
    type,
    inputProps,
    style,
    maxWidth,
    id,
    unit,
    icon,
    button,
    numberControl,
    onNumberControlError,
    onClick,
    tooltip,
    decimalPlaces
  } = _ref;
  const [hasFocus, setHasFocus] = useState(false);
  const inputRef = useRef(null);
  const generatedId = `tui-input-${useId()}`;
  const innerId = id || generatedId;
  const divRef = useRef(null);
  useEventListener(divRef.current, "focusout", event => {
    const focusedOnChild = () => divRef.current.contains(event.relatedTarget);
    if (disabled || readOnly || focusedOnChild()) return;
    setHasFocus(false);
    onBlur(event);
  }, [onBlur, disabled, readOnly]);
  const handleWheel = event => {
    if (type === "number" || numberControl) {
      event.target.blur();
    }
  };
  const renderedIcon = useIcon(icon.render);
  function renderIcon() {
    return renderedIcon;
  }
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", {
      className: classnames(`${prefixClassName} ${className}`, {
        "has-error": error
      }),
      tabIndex: -1,
      style: {
        ...style,
        maxWidth
      },
      ref: divRef,
      onFocus: e => {
        if (disabled || readOnly) return;
        if (!hasFocus) {
          setHasFocus(true);
          onFocus(e);
        }
      },
      onClick: onClick
    }, label && /*#__PURE__*/React.createElement(Label, _extends({
      className: classnames({
        "read-only": readOnly
      }),
      htmlFor: innerId,
      required: required
    }, tooltip && {
      tooltip
    }), label), /*#__PURE__*/React.createElement("div", {
      className: classnames(`${prefixClassName}-wrapper`, {
        "has-error": error,
        focus: hasFocus,
        disabled,
        "read-only": readOnly
      })
    }, button.render !== undefined && button.position === "left" ? button.render() : null, unit.label && unit.position === "left" ? /*#__PURE__*/React.createElement("div", {
      className: `${prefixClassName}-unit`
    }, unit.label) : null, numberControl ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `${prefixClassName}-number-control`,
      onClick: () => {
        if (!disabled && !readOnly) {
          const newValue = parseFloat(inputRef.current.value || 0) - 1;
          if (inputProps.min === undefined || newValue >= Number(inputProps.min)) {
            inputRef.current.value = newValue;
            onChange({
              target: {
                name,
                value: newValue
              }
            });
          } else {
            onNumberControlError("-");
          }
        }
      }
    }, /*#__PURE__*/React.createElement(Remove, null)) : null, /*#__PURE__*/React.createElement("div", {
      className: classnames(`${prefixClassName}-icon-wrapper`, {
        fluid,
        unit: unit.label,
        button: button.render !== undefined,
        icon: icon.render !== undefined,
        right: icon.render !== undefined && icon.position !== "left" || unit.label && unit.position !== "left" || button.render !== undefined && button.position !== "left",
        left: icon.render !== undefined && icon.position === "left" || unit.label && unit.position === "left" || button.render !== undefined && button.position === "left"
      })
    }, icon.render !== undefined && icon.position === "left" ? renderIcon() : null, /*#__PURE__*/React.createElement("input", _extends({
      ref: elRef => {
        inputRef.current = elRef;
        if (typeof ref === "function") {
          ref(elRef);
        } else if (ref) {
          // eslint-disable-next-line no-param-reassign
          ref.current = elRef;
        }
      },
      className: classnames(className, {
        "has-error": error,
        [`${prefixClassName}-number-control`]: numberControl
      }),
      onWheel: handleWheel,
      onChange: onChange,
      name: name,
      placeholder: placeholder,
      disabled: disabled || readOnly,
      type: type,
      id: innerId
    }, inputProps, {
      value: value,
      onPaste: e => {
        if (type !== "number") return;
        const value = e.clipboardData.getData("Text");
        if (!/^[0-9.]*$/.test(value) || !hasXDecimals(value, decimalPlaces)) {
          e.preventDefault();
        }
      },
      onKeyDown: e => {
        if (type === "number") {
          const pressedNumKey = /^[0-9.-]*$/.test(e.key);
          if (!(pressedNumKey || e.keyCode >= 3 && e.keyCode <= 47) && !e.ctrlKey && !e.altKey) {
            e.preventDefault();
            e.stopPropagation();
          } else if (pressedNumKey && decimalPlaces !== undefined) {
            if (decimalPlaces === 0 && e.key === "." || !hasXDecimals(e.currentTarget.value + e.key, decimalPlaces) || e.target.value !== "" && e.key === "-") {
              e.preventDefault();
              e.stopPropagation();
            }
          }
        }
        if (inputProps.onKeyDown !== undefined) {
          inputProps.onKeyDown(e);
        }
      }
    })), icon.render !== undefined && icon.position !== "left" ? renderIcon() : null), numberControl ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `${prefixClassName}-number-control`,
      onClick: () => {
        if (!disabled && !readOnly) {
          const newValue = parseFloat(inputRef.current.value || 0) + 1;
          if (inputProps.max === undefined || newValue <= Number(inputProps.max)) {
            inputRef.current.value = newValue;
            onChange({
              target: {
                name,
                value: newValue
              }
            });
          } else {
            onNumberControlError("+");
          }
        }
      }
    }, /*#__PURE__*/React.createElement(Add, null)) : null, unit.label && unit.position !== "left" ? /*#__PURE__*/React.createElement("div", {
      className: `${prefixClassName}-unit`
    }, unit.label) : null, button.render !== undefined && button.position !== "left" ? button.render() : null), tip ? /*#__PURE__*/React.createElement("div", {
      className: `${prefixClassName}-tip`
    }, tip) : null, error && !isEmpty(error) ? /*#__PURE__*/React.createElement("div", {
      className: "tui-invalid-feedback",
      children: error
    }) : "")
  );
});
Input.displayName = "Input";
export const InputDefaultProps = {
  prefixClassName: "tui-input",
  disabled: false,
  required: false,
  fluid: false,
  readOnly: false,
  style: {},
  onFocus: () => {},
  onBlur: () => {},
  id: undefined,
  className: "",
  type: undefined,
  error: undefined,
  name: undefined,
  value: undefined,
  label: undefined,
  tip: undefined,
  onChange: () => {},
  placeholder: undefined,
  maxWidth: undefined,
  numberControl: false,
  unit: {
    label: "",
    position: "right"
  },
  icon: {
    render: undefined,
    position: "right"
  },
  button: {
    render: undefined,
    position: "right"
  },
  inputProps: {},
  onNumberControlError: noop,
  onClick: noop,
  tooltip: "",
  decimalPlaces: undefined
};
Input.defaultProps = InputDefaultProps;
export const InputPropTypes = {
  prefixClassName: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  tip: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  fluid: PropTypes.bool,
  readOnly: PropTypes.bool,
  style: PropTypes.object,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  numberControl: PropTypes.bool,
  unit: PropTypes.shape({
    position: PropTypes.string,
    label: PropTypes.string
  }),
  icon: PropTypes.shape({
    position: PropTypes.string,
    render: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
  }),
  button: PropTypes.shape({
    position: PropTypes.string,
    render: PropTypes.func
  }),
  inputProps: PropTypes.object,
  onNumberControlError: PropTypes.func,
  onClick: PropTypes.func,
  tooltip: PropTypes.string,
  decimalPlaces: PropTypes.number
};
Input.propTypes = InputPropTypes;
export default Input;
//# sourceMappingURL=Input.js.map